import { faBan } from '@fortawesome/free-solid-svg-icons';

import Button from '@/components/Button';
import Stack from '@/components/Stack';
import { Body, Subheading } from '@/components/Typography';
import Icon from '@/elements/Icon';
import Text from '@/elements/Text';
import styled from '@/styles';
import { WorkerDetail } from '@/services/workersV2API';

type Props = {
  worker: Pick<
    WorkerDetail,
    | 'is_suspended'
    | 'suspension_type'
    | 'suspension_ends_at'
    | 'suspension_worker_visible_reason'
    | 'active_suspension'
    | 'active'
  >;
  onLiftSuspension?: () => void;
  onReactivate?: () => void;
  liftingSuspension?: boolean;
  reactivating?: boolean;
};

const Wrapper = styled('div', {
  alignItems: 'flex-start',
  display: 'flex',
  gap: '1rem',
  padding: '1rem',
  backgroundColor: '$dangerA8',
  border: '1px solid $dangerLightest',
  borderRadius: '$lg',
  color: '#CF040C',
  width: '100%',
});

const formatSuspensionDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const WorkerSuspensionBanner = ({
  worker,
  onLiftSuspension,
  onReactivate,
  liftingSuspension = false,
  reactivating = false,
}: Props) => {
  if (!worker.is_suspended && worker.active) return null;

  const isSuspended = worker.is_suspended;
  const isPermanent = worker.suspension_type === 'permanent';
  const isWorkerRequest =
    worker.active_suspension?.worker_visible_reason === 'worker_request';

  let title: string;
  let description: React.ReactNode;

  if (isSuspended && !isPermanent) {
    title = 'Account Suspended';
    const reason = worker.suspension_worker_visible_reason;
    const endDate = formatSuspensionDate(worker.suspension_ends_at);
    description = (
      <>
        Account temporarily suspended due to{' '}
        {reason && <strong>{reason}</strong>}
        {endDate && (
          <>
            . Account will be re-enabled on <strong>{endDate}</strong>
          </>
        )}
        .
      </>
    );
  } else if (isSuspended && isPermanent && !isWorkerRequest) {
    title = 'Account Disabled';
    const reason = worker.suspension_worker_visible_reason;
    description = reason ? (
      <>
        Account permanently disabled due to <strong>{reason}</strong>. Access to
        the GravyWork platform is no longer available.
      </>
    ) : (
      'Account permanently disabled due to a violation. Access to the GravyWork platform is no longer available.'
    );
  } else if (isSuspended && isPermanent && isWorkerRequest) {
    title = 'Account Disabled';
    description = 'Account permanently disabled by GravyWorker.';
  } else {
    title = 'Account Disabled';
    description = 'Account is currently disabled.';
  }

  return (
    <Wrapper>
      <Text color="inherit" size="lg">
        <Icon fixedWidth icon={faBan} />
      </Text>
      <Stack vertical align="start" gap={4}>
        <Subheading>
          <strong>{title}</strong>
        </Subheading>
        <Body>{description}</Body>
        {worker.is_suspended &&
          worker.active_suspension &&
          onLiftSuspension && (
            <div style={{ marginTop: '8px' }}>
              <Button
                a11yLabel="Unsuspend worker"
                appearance="outline"
                isLoading={liftingSuspension}
                label="Unsuspend Worker"
                status="success"
                onClick={onLiftSuspension}
              />
            </div>
          )}
        {!worker.active && !worker.is_suspended && onReactivate && (
          <div style={{ marginTop: '8px' }}>
            <Button
              a11yLabel="Reactivate worker"
              appearance="outline"
              isLoading={reactivating}
              label="Reactivate Worker"
              status="success"
              onClick={onReactivate}
            />
          </div>
        )}
      </Stack>
    </Wrapper>
  );
};

export default WorkerSuspensionBanner;
